// Angular
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS
import { Observable } from 'rxjs';

// App environment
import { environment } from '@env/environment';

// App types
import { SKIP_WORKSPACE_HEADER } from '@app/core/api/context/http-context.tokens';
import { ApiRequestConfig, ResponseType } from '@app/core/api/types';


@Injectable()
export class ApiExecutor {
  private baseUrl: string;

  constructor(private http: HttpClient) {
    this.baseUrl = environment.apiUrl || '';
  }


  /** @deprecated Prefer `@ApiController(path, { baseUrl })` so each service keeps its own host. */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }


  getBaseUrl(): string {
    return this.baseUrl;
  }


  execute<T = any>(config: ApiRequestConfig): Observable<T> {
    const url = this.buildUrl(config.url, config.baseUrl);
    const options = this.buildOptions(config);

    switch (config.method.toUpperCase()) {
      case 'GET':
        return this.http.get<T>(url, options) as Observable<T>;
      case 'POST':
        return this.http.post<T>(url, config.body, options) as Observable<T>;
      case 'PUT':
        return this.http.put<T>(url, config.body, options) as Observable<T>;
      case 'DELETE':
        return this.http.delete<T>(url, { ...options, body: config.body }) as Observable<T>;
      case 'PATCH':
        return this.http.patch<T>(url, config.body, options) as Observable<T>;
      default:
        throw new Error(`Unsupported HTTP method: ${config.method}`);
    }
  }


  private buildUrl(path: string, baseUrl?: string): string {
    const cleanBaseUrl = (baseUrl ?? this.baseUrl).replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${cleanBaseUrl}${cleanPath}`;
  }


  private buildOptions(config: ApiRequestConfig): object {
    const options: {
      headers?: HttpHeaders | { [header: string]: string | string[] };
      params?: HttpParams;
      responseType?: ResponseType;
      reportProgress?: boolean;
      observe?: ApiRequestConfig['observe'];
      context?: HttpContext;
    } = {
    };

    if (config.headers) {
      options.headers = config.headers;
    }

    if (config.responseType) {
      options.responseType = config.responseType;
    }

    if (config.reportProgress) {
      options.reportProgress = true;
    }

    if (config.observe) {
      options.observe = config.observe;
    }

    if (config.params) {
      let httpParams = new HttpParams();
      Object.keys(config.params).forEach(key => {
        httpParams = this.appendQueryParam(httpParams, key, config.params![key]);
      });
      options.params = httpParams;
    }

    if (config.skipWorkspaceHeader) {
      options.context = new HttpContext().set(SKIP_WORKSPACE_HEADER, true);
    }

    return options;
  }

  private appendQueryParam(httpParams: HttpParams, key: string, value: unknown): HttpParams {
    if (value === null || value === undefined) {
      return httpParams;
    }
    if (Array.isArray(value)) {
      return value.reduce(
        (params, item) => this.appendQueryParam(params, key, item),
        httpParams,
      );
    }
    return httpParams.append(key, String(value));
  }
}

